import { CREATE_DIALOG_ACTION_CLASS } from 'components/buttons/CancelButton';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { useCreateDialogRequest } from './CreateDialogContext';
import type { CreateRecordType } from './recordTypes';

const CLOSE_QUESTION = {
    title: 'create-dialog.close-question.title',
    text: 'create-dialog.close-question.text',
    keep: 'create-dialog.close-question.keep-editing',
    save: 'create-dialog.close-question.save',
    discard: 'create-dialog.close-question.discard',
};

const DISCARD_QUESTION = {
    ...CLOSE_QUESTION,
    title: 'create-dialog.close-question.discard-title',
    text: 'create-dialog.close-question.discard-text',
};

const SWITCH_QUESTION = {
    title: 'create-dialog.switch-question.title',
    text: 'create-dialog.switch-question.text',
    keep: 'create-dialog.switch-question.go-back',
    save: 'create-dialog.switch-question.save',
    discard: 'create-dialog.switch-question.discard',
};

const ACTION_CLASS = 'px-4 font-semibold';

type Props = {
    /** The record of the creation that waits to open; `null` for the question the dialog asks as it closes. */
    next: CreateRecordType | null;
    /** Keep editing, Go back, and Escape. */
    onKeep: () => void;
    onSave: () => void;
    onDiscard: () => void;
};

/**
 * What becomes of the creation something was typed in, asked above the dialog, or above the page while the
 * dialog is a tab: as it closes, or as another creation waits to take its place, since one exists at a time. It
 * is saved as a draft or discarded; where the form offers no draft, discarding is the one way on. The answer that
 * changes nothing has the focus, and Escape gives it.
 */
export function CreateDialogQuestion({ next, onKeep, onSave, onDiscard }: Props) {
    const intl = useIntl();
    const request = useCreateDialogRequest();
    const keepRef = useRef<HTMLButtonElement>(null);

    if (!request) {
        return null;
    }
    // The nouns are the keys `create-dialog.record.*`, one per record type.
    const noun = (record: CreateRecordType) => intl.formatMessage({ id: `create-dialog.record.${record}` });
    // Where the creation started, for the message to word: `origin` selects, `name` and `number` are its value.
    const { origin } = request;
    const canSave = request.saveDraft !== null;
    // Closing a creation that cannot be saved as a draft only asks whether to discard it.
    const ids = next ? SWITCH_QUESTION : canSave ? CLOSE_QUESTION : DISCARD_QUESTION;
    const values = {
        record: noun(request.record),
        origin: origin?.origin ?? 'none',
        name: origin?.value ?? '',
        number: origin?.value ?? '',
        next: next ? noun(next) : '',
    };

    const keep = (
        <Button
            ref={keepRef}
            variant={next ? 'secondary' : 'outline'}
            size="lg"
            className={next ? ACTION_CLASS : CREATE_DIALOG_ACTION_CLASS}
            onClick={onKeep}
        >
            <FormattedMessage id={ids.keep} values={values} />
        </Button>
    );
    const save = canSave ? (
        <Button
            variant={next ? 'outline' : 'secondary'}
            size="lg"
            className={next ? CREATE_DIALOG_ACTION_CLASS : ACTION_CLASS}
            onClick={onSave}
        >
            <FormattedMessage id={ids.save} />
        </Button>
    ) : null;
    const discard = (
        <Button
            variant="destructive"
            size="lg"
            className={cn(ACTION_CLASS, 'bg-danger-tint text-danger-ink hover:bg-danger-tint hover:brightness-95')}
            onClick={onDiscard}
        >
            <FormattedMessage id={ids.discard} values={values} />
        </Button>
    );

    return (
        <AlertDialog
            open
            onOpenChange={(open) => {
                if (!open) {
                    onKeep();
                }
            }}
        >
            <AlertDialogContent
                className={cn('gap-5', next && 'data-[size=default]:sm:max-w-[560px]')}
                initialFocus={keepRef}
            >
                <AlertDialogHeader className="gap-2">
                    <AlertDialogTitle className="text-dialog-title font-semibold">
                        <FormattedMessage id={ids.title} values={values} />
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-foreground-secondary">
                        <FormattedMessage id={ids.text} values={values} />
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    {next ? (
                        <>
                            {discard}
                            {save}
                            {keep}
                        </>
                    ) : (
                        <>
                            {keep}
                            {discard}
                            {save}
                        </>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
